import React, { useState, useEffect } from 'react';

function ProgramariNew() {
    // State for patient information (bare minimum fields)
    const [patientData, setPatientData] = useState({
        nume: '',
        prenume: '',
        email: '',
        telefon: '',
        sex: ''
    });

    // State for appointment details
    const [appointmentData, setAppointmentData] = useState({
        id_specialitate: '',
        id_medic: '',
        data: '',
        ora: '',
        observatii: ''
    });

    // State for dropdown data
    const [specialties, setSpecialties] = useState([]);
    const [doctors, setDoctors] = useState([]);

    // UI state
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    // Load specialties on component mount
    useEffect(() => {
        loadSpecialties();
    }, []);

    // Load doctors when specialty changes
    useEffect(() => {
        if (appointmentData.id_specialitate) {
            loadDoctorsBySpecialty(appointmentData.id_specialitate);
        } else {
            setDoctors([]);
        }
    }, [appointmentData.id_specialitate]);

    const loadSpecialties = async () => {
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_API_URL}/v1/public/specialitati/list`;
            console.log('Fetching specialties from:', url);

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response body:', errorText);
                throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
            }

            const data = await response.json();
            console.log('Specialties data:', data);
            setSpecialties(data.items || []);
        } catch (err) {
            console.error('Error loading specialties:', err);
            setError(`Eroare la încărcarea specialităților: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const loadDoctorsBySpecialty = async (specialtyId) => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/medici/list?id_specialitate=${specialtyId}`, {
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDoctors(data.items || []);
        } catch (err) {
            console.error('Error loading doctors:', err);
            setError('Eroare la încărcarea medicilor');
        } finally {
            setLoading(false);
        }
    };

    const handlePatientChange = (field, value) => {
        setPatientData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
        setMessage('');
    };

    const handleAppointmentChange = (field, value) => {
        setAppointmentData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
        setMessage('');
    };

    const validatePatientInfo = () => {
        if (!patientData.nume.trim()) {
            setError('Numele este obligatoriu');
            return false;
        }
        if (!patientData.prenume.trim()) {
            setError('Prenumele este obligatoriu');
            return false;
        }
        if (!patientData.email.trim()) {
            setError('Email-ul este obligatoriu');
            return false;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(patientData.email.trim())) {
            setError('Email-ul trebuie să fie într-un format valid (ex: nume@domain.com)');
            return false;
        }
        if (!patientData.telefon.trim()) {
            setError('Numărul de telefon este obligatoriu');
            return false;
        }
        // Validate phone number format (exactly 10 digits)
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(patientData.telefon.trim())) {
            setError('Numărul de telefon trebuie să conțină exact 10 cifre');
            return false;
        }
        if (!patientData.sex) {
            setError('Sexul este obligatoriu');
            return false;
        }
        return true;
    };

    const validateAppointmentDetails = () => {
        if (!appointmentData.id_specialitate) {
            setError('Specialitatea este obligatorie');
            return false;
        }
        if (!appointmentData.id_medic) {
            setError('Medicul este obligatoriu');
            return false;
        }
        if (!appointmentData.data) {
            setError('Data este obligatorie');
            return false;
        }
        if (!appointmentData.ora) {
            setError('Ora este obligatorie');
            return false;
        }
        return true;
    };

    const validateAllData = () => {
        if (!validatePatientInfo()) {
            return false;
        }
        if (!validateAppointmentDetails()) {
            return false;
        }
        return true;
    };

    const createAppointment = async () => {
        if (!validateAllData()) {
            return;
        }

        try {
            setLoading(true);

            // Step 1: Create patient
            const patientResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/pacienti`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...patientData,
                    nationalitate: 'romania'
                })
            });

            if (!patientResponse.ok) {
                throw new Error('Eroare la crearea pacientului');
            }

            const patientDataResult = await patientResponse.json();

            if (!patientDataResult.ok || !patientDataResult.item) {
                throw new Error(patientDataResult.error || 'Eroare la crearea pacientului');
            }

            const patientId = patientDataResult.item.id;

            // Step 2: Create appointment
            const appointmentPayload = {
                id_medic: parseInt(appointmentData.id_medic),
                id_pacient: parseInt(patientId),
                data: appointmentData.data,
                ora: appointmentData.ora,
                durata: 30, // Default duration
                observatii: appointmentData.observatii || ''
            };

            const appointmentResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/programari/insert`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(appointmentPayload)
            });

            if (!appointmentResponse.ok) {
                const errorText = await appointmentResponse.text();
                throw new Error(`Eroare la crearea programării: ${errorText}`);
            }

            const appointmentResult = await appointmentResponse.json();

            if (appointmentResult.ok) {
                setMessage('Programarea a fost creată cu succes! Veți primi o confirmare în curând.');
                // Reset form
                setPatientData({
                    nume: '',
                    prenume: '',
                    email: '',
                    telefon: '',
                    sex: ''
                });
                setAppointmentData({
                    id_specialitate: '',
                    id_medic: '',
                    data: '',
                    ora: '',
                    observatii: ''
                });
            } else {
                setError(appointmentResult.error || 'Eroare la crearea programării');
            }

        } catch (err) {
            console.error('Error creating appointment:', err);
            setError(err.message || 'Eroare la crearea programării');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
            <h1 style={{ textAlign: 'center', marginBottom: '2rem', color: '#006666', fontSize: '2rem' }}>
                Programare Online
            </h1>

            <p style={{ textAlign: 'center', marginBottom: '2rem', color: '#666', fontSize: '1.1rem' }}>
                Programați-vă ușor și rapid la clinica noastră medicală
            </p>



            {error && (
                <div style={{
                    background: '#ffebee',
                    color: '#c62828',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #ffcdd2',
                    fontSize: '0.9rem'
                }}>
                    {error}
                </div>
            )}

            {message && (
                <div style={{
                    background: '#e8f5e8',
                    color: '#2e7d32',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #c8e6c9',
                    fontSize: '0.9rem'
                }}>
                    {message}
                </div>
            )}

            <div style={{
                background: 'white',
                padding: '1rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                maxWidth: '700px'
            }}>

                {/* Ultra-Compact Single Column Layout */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                    {/* Combined Patient and Appointment Form */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>

                        {/* Patient Information */}
                        <div>
                            <h3 style={{ marginBottom: '0.75rem', color: '#333', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                Date personale
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Nume *
                                        </label>
                                        <input
                                            type="text"
                                            value={patientData.nume}
                                            onChange={(e) => handlePatientChange('nume', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.375rem',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                height: '32px'
                                            }}
                                            placeholder="Nume"
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Prenume *
                                        </label>
                                        <input
                                            type="text"
                                            value={patientData.prenume}
                                            onChange={(e) => handlePatientChange('prenume', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.375rem',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                height: '32px'
                                            }}
                                            placeholder="Prenume"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={patientData.email}
                                        onChange={(e) => handlePatientChange('email', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.375rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem',
                                            height: '32px'
                                        }}
                                        placeholder="email@example.com"
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Telefon *
                                        </label>
                                        <input
                                            type="tel"
                                            value={patientData.telefon}
                                            onChange={(e) => handlePatientChange('telefon', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.375rem',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                height: '32px'
                                            }}
                                            placeholder="07xxxxxxxx"
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Sex *
                                        </label>
                                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem' }}>
                                                <input
                                                    type="radio"
                                                    name="sex"
                                                    value="F"
                                                    checked={patientData.sex === 'F'}
                                                    onChange={(e) => handlePatientChange('sex', e.target.value)}
                                                />
                                                Feminin
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem' }}>
                                                <input
                                                    type="radio"
                                                    name="sex"
                                                    value="M"
                                                    checked={patientData.sex === 'M'}
                                                    onChange={(e) => handlePatientChange('sex', e.target.value)}
                                                />
                                                Masculin
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Appointment Details */}
                        <div>
                            <h3 style={{ marginBottom: '0.75rem', color: '#333', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                Detalii programare
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                        Specialitate *
                                    </label>
                                    <select
                                        value={appointmentData.id_specialitate}
                                        onChange={(e) => handleAppointmentChange('id_specialitate', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.375rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem',
                                            height: '32px'
                                        }}
                                        disabled={loading}
                                    >
                                        <option value="">Selectați specialitatea</option>
                                        {specialties.map(specialty => (
                                            <option key={specialty.id} value={specialty.id}>
                                                {specialty.denumire}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {appointmentData.id_specialitate && (
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Medic *
                                        </label>
                                        <select
                                            value={appointmentData.id_medic}
                                            onChange={(e) => handleAppointmentChange('id_medic', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.375rem',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                height: '32px'
                                            }}
                                            disabled={loading}
                                        >
                                            <option value="">Selectați medicul</option>
                                            {doctors.map(doctor => (
                                                <option key={doctor.id} value={doctor.id}>
                                                    {doctor.nume} {doctor.prenume}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Data *
                                        </label>
                                        <input
                                            type="date"
                                            value={appointmentData.data}
                                            onChange={(e) => handleAppointmentChange('data', e.target.value)}
                                            min={new Date().toISOString().split('T')[0]}
                                            style={{
                                                width: '100%',
                                                padding: '0.375rem',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                height: '32px'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Ora *
                                        </label>
                                        <input
                                            type="time"
                                            value={appointmentData.ora}
                                            onChange={(e) => handleAppointmentChange('ora', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.375rem',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                height: '32px'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                        Observații (opțional)
                                    </label>
                                    <textarea
                                        value={appointmentData.observatii}
                                        onChange={(e) => handleAppointmentChange('observatii', e.target.value)}
                                        placeholder="Menționați orice detalii..."
                                        style={{
                                            width: '100%',
                                            padding: '0.375rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem',
                                            minHeight: '40px',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                        <button
                            onClick={createAppointment}
                            disabled={loading}
                            style={{
                                padding: '0.625rem 2rem',
                                background: loading ? '#ccc' : '#006666',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                fontWeight: 'bold',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                minWidth: '180px'
                            }}
                        >
                            {loading ? 'Se programează...' : 'Programează-mă'}
                        </button>
                    </div>
                </div>

            </div>

            <div style={{ textAlign: 'center', marginTop: '2rem', color: '#666', fontSize: '0.9rem' }}>
                <p>* Câmpuri obligatorii</p>
                <p>Vă vom contacta pentru confirmarea programării în maximum 24 de ore.</p>
            </div>
        </div>
    );
}

export default ProgramariNew;
